import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { useT } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, BookOpen, Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { generateReportPDF } from '../lib/pdf';
import FileUpload from '../components/FileUpload';
import EditEntityDialog from '../components/EditEntityDialog';

const MANAGE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'];

export default function SOPs() {
  const t = useT();
  const { user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [company, setCompany] = useState(null);
  const [category, setCategory] = useState('sop');
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [newDoc, setNewDoc] = useState({ title: '', category: 'sop', content: '', version: '1.0', tags: '', file_url: '' });

  useEffect(() => { fetchData(); }, [category]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        axios.get(`${API}/sop-documents?category=${category}`, { withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setItems(d.data);
      setCompany(c.data.find(x => x.id === user?.company_id) || c.data[0]);
    } catch { toast.error('Failed'); }
    setLoading(false);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/sop-documents`, {
        ...newDoc, category,
        tags: newDoc.tags.split(',').map(t => t.trim()).filter(Boolean)
      }, { withCredentials: true });
      toast.success('Document saved');
      setShowNew(false);
      setNewDoc({ title: '', category: 'sop', content: '', version: '1.0', tags: '', file_url: '' });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete document?')) return;
    try { await axios.delete(`${API}/sop-documents/${id}`, { withCredentials: true }); fetchData(); setSelected(null); }
    catch { toast.error('Failed'); }
  };

  const downloadPDF = (d) => {
    generateReportPDF({
      title: d.title,
      subtitle: `${d.category.toUpperCase()} · v${d.version}`,
      company,
      sections: [{ paragraph: d.content }]
    });
  };

  const tabs = [
    { id: 'sop', label: 'Standard Operating Procedures' },
    { id: 'sla', label: 'Service Level Agreements' },
    { id: 'emergency_protocol', label: 'Emergency Protocols' }
  ];

  return (
    <Layout>
      <div data-testid="sops-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">SOPs &middot; SLAs &middot; Emergency Protocols</h1>
            <p className="text-sm text-muted-foreground">Document library for your operations teams</p>
          </div>
          {MANAGE.includes(user?.role) && (
            <Dialog open={showNew} onOpenChange={setShowNew}>
              <DialogTrigger asChild><Button data-testid="new-sop-btn" className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1" />New Document</Button></DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>New {category.toUpperCase()} Document</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div><Label>Title</Label><Input data-testid="sop-title" value={newDoc.title} onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Version</Label><Input value={newDoc.version} onChange={(e) => setNewDoc({ ...newDoc, version: e.target.value })} /></div>
                    <div><Label>Tags (comma-sep)</Label><Input value={newDoc.tags} onChange={(e) => setNewDoc({ ...newDoc, tags: e.target.value })} placeholder="HVAC, safety" /></div>
                  </div>
                  <div><Label>Content</Label><Textarea rows={10} data-testid="sop-content" value={newDoc.content} onChange={(e) => setNewDoc({ ...newDoc, content: e.target.value })} placeholder="Procedure steps, SLA terms, emergency response..." /></div>
                  <div><Label>Attach file (PDF / image)</Label>
                    <FileUpload
                      value={newDoc.file_url ? [newDoc.file_url] : []}
                      onChange={(urls) => setNewDoc({ ...newDoc, file_url: urls[0] || '' })}
                      maxFiles={1}
                      accept="application/pdf,image/*"
                    />
                  </div>
                  <Button data-testid="submit-sop-btn" onClick={handleCreate} disabled={!newDoc.title || !newDoc.content} className="w-full bg-teal-600 hover:bg-teal-700">Save Document</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="flex gap-2 flex-wrap border-b border-slate-200">
          {tabs.map(t => (
            <button key={t.id} data-testid={`sop-tab-${t.id}`} onClick={() => setCategory(t.id)}
              className={`px-4 py-2 text-sm font-medium transition border-b-2 ${category === t.id ? 'border-teal-600 text-teal-600' : 'border-transparent text-slate-600 hover:text-slate-900'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading…</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map(d => (
              <Card key={d.id} data-testid={`sop-card-${d.id}`} className="p-5 hover:shadow-lg transition cursor-pointer" onClick={() => setSelected(d)}>
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 bg-teal-100 rounded-lg"><BookOpen className="w-5 h-5 text-teal-700" /></div>
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold">v{d.version}</span>
                </div>
                <h3 className="font-semibold text-foreground mb-1">{d.title}</h3>
                <p className="text-xs text-muted-foreground line-clamp-2">{d.content?.slice(0, 100)}...</p>
                {d.tags?.length > 0 && <div className="flex flex-wrap gap-1 mt-3">{d.tags.map(t => <span key={t} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{t}</span>)}</div>}
              </Card>
            ))}
            {items.length === 0 && <Card className="p-10 text-center col-span-full"><BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-muted-foreground">No documents</p></Card>}
          </div>
        )}

        {selected && (
          <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{selected.title} <span className="text-xs font-normal text-slate-500">v{selected.version}</span></DialogTitle>
              </DialogHeader>
              <div className="prose prose-sm max-w-none whitespace-pre-wrap text-slate-700 mt-4">{selected.content}</div>
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button data-testid="download-sop-pdf" onClick={() => downloadPDF(selected)} variant="outline"><Download className="w-4 h-4 mr-1" />Download PDF</Button>
                {MANAGE.includes(user?.role) && (
                  <>
                    <EditEntityDialog
                      item={selected}
                      endpoint="sop-documents"
                      title="Edit Document"
                      onSaved={() => { fetchData(); setSelected(null); }}
                      buttonClassName="border border-amber-300 text-amber-700 hover:bg-amber-50"
                      buttonVariant="outline"
                      fields={[
                        { key: 'title', label: 'Title' },
                        { key: 'version', label: 'Version' },
                        { key: 'category', label: 'Category', type: 'select', options: [
                          { value: 'sop', label: 'SOP' },
                          { value: 'sla', label: 'SLA' },
                          { value: 'emergency', label: 'Emergency' },
                          { value: 'policy', label: 'Policy' },
                        ]},
                        { key: 'content', label: 'Content', type: 'textarea', rows: 8 },
                        { key: 'file_url', label: 'File URL (optional)', placeholder: 'Pasted/uploaded file link' },
                      ]}
                    />
                    <Button data-testid="delete-sop-btn" onClick={() => handleDelete(selected.id)} variant="outline" className="text-red-500 border-red-300"><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}
